"use client"

import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { memo, useCallback, useId, useRef, useState } from "react"
import { cn } from "@strivelabs/ui-core"
import { DURATION_BASE, EASE_OUT } from "@strivelabs/ui-core/v2"
import { useSidebar } from "./context"
import { Disclosure } from "./disclosure"
import { NavSectionLabel } from "./nav-section"
import type { NavNode, NavSection } from "./types"

export interface NavTreeProps {
  sections: NavSection[]
  depth?: number
}

/** Renders a run of sections. Recursion happens through NavTreeItem. */
export function NavTree({ sections, depth = 0 }: NavTreeProps) {
  return (
    <>
      {sections.map(section => (
        <div key={section.id}>
          {/* A labelled group is a break in the list, so it gets a rule as well
              as a heading — the label alone reads as a floating caption. */}
          {section.label ? (
            <>
              <div aria-hidden className="mx-2 mt-2 h-px bg-v2-sidebar-border" />
              <NavSectionLabel label={section.label} />
            </>
          ) : null}
          <ul className="flex flex-col gap-0.5">
            {section.items.map(item => (
              <NavTreeItem key={item.id} node={item} depth={depth} />
            ))}
          </ul>
        </div>
      ))}
    </>
  )
}

interface NavTreeItemProps {
  node: NavNode
  depth: number
}

const NavTreeItem = memo(function NavTreeItem({ node, depth }: NavTreeItemProps) {
  const { isExpanded, toggleExpanded, currentPath, renderLink } = useSidebar()
  const reduce = useReducedMotion()
  const panelId = useId()
  const [loaded, setLoaded] = useState<NavSection[] | null>(null)
  const [loading, setLoading] = useState(false)
  const requested = useRef(false)

  const expanded = isExpanded(node.id)
  const expandable = node.children !== undefined || node.loadChildren !== undefined
  const children = node.children ?? loaded ?? []

  const active = node.match
    ? node.match(currentPath)
    : node.href !== undefined && node.href !== "#" && currentPath === node.href

  const onToggle = useCallback(() => {
    toggleExpanded(node.id)
    // `requested` rather than `loaded === null`, so a second click while the
    // first fetch is still in flight cannot start a duplicate request.
    if (node.loadChildren && !requested.current) {
      requested.current = true
      setLoading(true)
      node
        .loadChildren()
        .then(sections => setLoaded(sections))
        .catch(() => {
          // Release the guard so the next expand tries again. Without a catch
          // this was an unhandled rejection, and `requested` stayed set — the
          // node was left empty for the rest of the session with no way back.
          requested.current = false
        })
        .finally(() => setLoading(false))
    }
  }, [node, toggleExpanded])

  const rowClass = cn(
    depth === 0 ? "text-v2-base" : "text-v2-sm",
    "flex min-w-0 flex-1 items-center gap-2 rounded-v2-md py-1.5 pl-1 pr-2",
    "text-v2-sidebar-foreground/65 transition-colors hover:bg-v2-sidebar-accent hover:text-v2-sidebar-foreground",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring",
    active && "bg-v2-sidebar-accent font-medium text-v2-sidebar-accent-foreground",
    node.disabled && "pointer-events-none opacity-50",
  )

  const label = (
    <>
      {node.icon ? (
        <span className="grid size-4 shrink-0 place-items-center">{node.icon}</span>
      ) : null}
      <span className="truncate">{node.label}</span>
    </>
  )

  return (
    <li style={{ paddingInlineStart: depth === 0 ? 0 : 12 }}>
      <div className="flex items-center">
        {expandable ? (
          <Disclosure
            expanded={expanded}
            busy={loading}
            label={node.label}
            controls={expanded && children.length > 0 ? panelId : undefined}
            onToggle={onToggle}
          />
        ) : (
          <span aria-hidden className="w-[15px] shrink-0" />
        )}
        {node.href && node.href !== "#" && !node.disabled
          ? renderLink({
              href: node.href,
              className: rowClass,
              children: label,
              "aria-current": active ? "page" : undefined,
              onClick: node.onSelect,
            })
          : (
            <button
              type="button"
              onClick={node.onSelect}
              disabled={node.disabled}
              className={rowClass}
            >
              {label}
            </button>
          )}
      </div>

      <AnimatePresence initial={false}>
        {expanded && children.length > 0 ? (
          <motion.div
            id={panelId}
            key="children"
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={reduce ? { duration: 0 } : { duration: DURATION_BASE, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <NavTree sections={children} depth={depth + 1} />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </li>
  )
})
